import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Calendar, Clock, MapPin, Users, Crown, Shield } from "lucide-react";
import { EventWithAttendees } from "@/services/eventService";
import { useAuth } from "@/hooks/useAuth";
import { format } from "date-fns";
import EventGroupChatSimple from "./EventGroupChatSimple";

interface EventDetailsDialogProps {
  event: EventWithAttendees | null;
  isOpen: boolean;
  onClose: () => void;
  onApproveAttendee: (attendeeId: string) => void;
  onDeclineAttendee: (attendeeId: string) => void;
  onPromoteToAdmin: (attendeeId: string) => void;
  onRemoveAttendee: (attendeeId: string) => void;
  isUpdating?: boolean;
}

const EventDetailsDialog = ({
  event,
  isOpen,
  onClose,
  onApproveAttendee,
  onDeclineAttendee,
  onPromoteToAdmin,
  onRemoveAttendee,
  isUpdating
}: EventDetailsDialogProps) => {
  const { user } = useAuth();

  if (!event) return null;

  const isCreator = event.created_by === user?.id;
  const userAttendance = event.attendees.find(a => a.user_id === user?.id);
  const isUserAdmin = userAttendance?.is_admin || isCreator;
  const isApprovedAttendee = userAttendance?.status === "approved" || isCreator;

  const pendingAttendees = event.attendees.filter(a => a.status === "pending");
  const approvedAttendees = event.attendees.filter(a => a.status === "approved");

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl">{event.title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col h-[70vh] overflow-hidden">
          {/* Main content - takes 60% of height */}
          <div className="flex-[3] overflow-y-auto space-y-6 pr-2">
            {event.image_url && (
              <div className="aspect-video w-full overflow-hidden rounded-lg">
                <img
                  src={event.image_url}
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {event.description && (
              <div>
                <h3 className="font-semibold mb-2">Description</h3>
                <p className="text-gray-600">{event.description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-500" />
                <span>{format(new Date(event.event_date), "EEEE, MMMM dd, yyyy")}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-500" />
                <span>{event.event_time}</span>
              </div>
              
              <div className="flex items-center gap-2 md:col-span-2">
                <MapPin className="w-5 h-5 text-gray-500" />
                <span>{event.address}</span>
              </div>
            </div>

            {/* Approved Attendees */}
            {approvedAttendees.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Participants ({approvedAttendees.length})
                </h3>
                <div className="space-y-2">
                  {approvedAttendees.map((attendee) => (
                    <div key={attendee.id} className="flex items-center justify-between p-2 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>
                            {attendee.user.username?.[0]?.toUpperCase() || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{attendee.user.username || "Unknown User"}</p>
                          <div className="flex items-center gap-2">
                            {attendee.is_admin && (
                              <Badge variant="secondary" className="text-xs">
                                <Shield className="w-3 h-3 mr-1" />
                                Admin
                              </Badge>
                            )}
                            {event.created_by === attendee.user_id && (
                              <Badge variant="default" className="text-xs">
                                <Crown className="w-3 h-3 mr-1" />
                                Creator
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {isUserAdmin && event.created_by !== attendee.user_id && (
                        <div className="flex gap-2">
                          {!attendee.is_admin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onPromoteToAdmin(attendee.id)}
                              disabled={isUpdating}
                            >
                              Promote
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => onRemoveAttendee(attendee.id)}
                            disabled={isUpdating}
                          >
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Requests */}
            {isUserAdmin && pendingAttendees.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Pending Requests ({pendingAttendees.length})</h3>
                <div className="space-y-2">
                  {pendingAttendees.map((attendee) => (
                    <div key={attendee.id} className="flex items-center justify-between p-2 border rounded-lg bg-yellow-50">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>
                            {attendee.user.username?.[0]?.toUpperCase() || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <p className="font-medium">{attendee.user.username || "Unknown User"}</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => onApproveAttendee(attendee.id)}
                          disabled={isUpdating}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onDeclineAttendee(attendee.id)}
                          disabled={isUpdating}
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User's status */}
            {userAttendance && (
              <div className="p-3 border rounded-lg bg-gray-50">
                <p className="text-sm text-gray-600">
                  Your status: <Badge variant={
                    userAttendance.status === "approved" ? "default" : 
                    userAttendance.status === "pending" ? "secondary" : "destructive"
                  }>{userAttendance.status}</Badge>
                </p>
              </div>
            )}
          </div>

          {/* Group Chat - takes 40% of height */}
          {isApprovedAttendee && (
            <div className="flex-[2] border-t pt-4 mt-4">
              <EventGroupChatSimple event={event} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventDetailsDialog;
